import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router'
import { FaCheck, FaRegComments } from 'react-icons/fa'
import { Box, Stack, ResponsiveStack, Badge, Button, Heading, Text, Link, Select, Textarea, EmptyBox, SimpleTable, SimpleModal, SimpleTabs, UserChip, UserChooser, FormControl, Card, Icon, ConfirmButton, EmptyCard } from '@/components/lib'
import utils from '@/utils/utils'
import api from '../../../api'
import useStore from '../../../store'
import QueryComments from './Comments'

import QueriesImage from '../../../images/queries.png'

export default function List () {
  const [queries, setQueries] = useState([])
  const [statusFilter, setStatusFilter] = useState('open')
  const [assigneesFilter, setAssigneesFilter] = useState([])
  const [resolvingIds, setResolvingIds] = useState([])
  // Which query the manage modal is showing, held here rather than per row: now
  // that changing the status saves immediately, closing a query from inside the
  // modal would filter its row out of the table and unmount the modal with it.
  const [managedQueryId, setManagedQueryId] = useState(null)
  const [loaded, setLoaded] = useState(false)
  const [loadError, setLoadError] = useState()
  const { trial, getTrialUsers, dataTypes, user, userId, updateTrial } = useStore()
  const [searchParams, setSearchParams] = useSearchParams()
  const trialId = trial?._id
  const users = getTrialUsers(trialId)
  // Notifications about a query — an assignment, a comment, a mention — link
  // here naming the query, so it opens straight onto the conversation.
  const openQueryId = searchParams.get('query')

  useEffect(() => {
    if (!trialId) return
    api.queries.getForTrial(trialId, d => {
      setQueries(d.queries)
      setLoaded(true)
    }, err => {
      // Without this a refusal looked exactly like a study with no queries in
      // it, which is a confusing place to land from a notification link.
      setLoadError(err)
      setLoaded(true)
    })
  }, [trialId])

  // The sidebar's count comes from the trial, which is loaded once. While
  // someone is working through this page it would otherwise drift every time a
  // query is resolved or reopened, so keep it in step with what is on screen.
  useEffect(() => {
    if (!loaded || !trialId) return
    const open = queries.filter(q => q.status === 'open').length
    if (open !== trial?.openQueryCount) updateTrial(trialId, { openQueryCount: open })
  }, [loaded, queries, trialId, trial?.openQueryCount, updateTrial])

  // A link to a closed query would otherwise land on a list filtered to open
  // ones, so the query would be nowhere to be seen once the modal is closed.
  useEffect(() => {
    if (!openQueryId) return
    setManagedQueryId(openQueryId)
    const linked = queries.find(q => String(q._id) === openQueryId)
    if (linked && statusFilter && linked.status !== statusFilter) setStatusFilter('')
  }, [openQueryId, queries, statusFilter])

  function closeManager () {
    setManagedQueryId(null)
    if (!openQueryId) return
    const next = new URLSearchParams(searchParams)
    next.delete('query')
    setSearchParams(next, { replace: true })
  }

  function updateQuery (query) {
    const newQueries = queries.map(q => q._id === query._id ? query : q)
    setQueries(newQueries)
  }

  const canWriteQueries = utils.canWriteTrialQueries(user, trial)

  function canResolve (query) {
    if (!query || query.status !== 'open') return false
    if (canWriteQueries) return true
    const assignees = query.assignees || []
    return userId && assignees.includes(userId)
  }

  function resolveQuery (queryId) {
    if (!queryId) return
    if (resolvingIds.includes(queryId)) return
    setResolvingIds([...resolvingIds, queryId])
    api.queries.update(queryId, { status: 'closed' }, d => {
      setResolvingIds(ids => ids.filter(id => id !== queryId))
      updateQuery(d)
      utils.toast('success', 'Query resolved')
    }, err => {
      setResolvingIds(ids => ids.filter(id => id !== queryId))
      utils.toast('error', 'Unable to resolve query', err.message)
    })
  }

  // Looked up from every query rather than the filtered ones, so a query stays
  // open in the modal after a change that no longer matches the filters.
  const managedQuery = queries.find(q => String(q._id) === String(managedQueryId))

  let filteredQueries = queries
  if (statusFilter) filteredQueries = filteredQueries.filter(q => q.status === statusFilter)
  if (assigneesFilter?.length > 0) {
    filteredQueries = filteredQueries.filter(q => {
      const qAssignees = q.assignees || []
      return assigneesFilter.every(a => qAssignees.includes(a))
    })
  }

  return (
    <Stack gap={5}>
      <Box>
        <Heading as='h2' size='lg'>Trial queries</Heading>
        <Text>Questions or requests for clarification about the trial.</Text>
      </Box>
      {loadError &&
        <EmptyCard icon='box' title='No access' description={loadError.message || "You don't have permission to view this study's queries."} />}
      {!loadError && queries.length > 0
        ? (
          <Stack gap={5}>
            <Stack direction='row' alignItems='center'>
              <Select size='sm' value={statusFilter} onChange={e => setStatusFilter(e.target.value)} w='200px'>
                <option value=''>All statuses</option>
                <option value='open'>Open queries</option>
                <option value='closed'>Resolved queries</option>
              </Select>
              <Box w='300px'>
                <UserChooser
                  size='sm'
                  multiple
                  placeholder='All assignees'
                  users={users}
                  selectedUsers={assigneesFilter}
                  onChosen={u => setAssigneesFilter(u || [])}
                />
              </Box>
            </Stack>

            {filteredQueries.length === 0
              ? (
                <EmptyBox image={QueriesImage} title='No queries match your filters' description='Try changing the filters or check back later.' />)
              : (
                <SimpleTable
                  asCard
                  tableProps={{ size: 'sm' }}
                  onRowClick={(row, index) => setManagedQueryId(filteredQueries[index]?._id)}
                  headers={['Title', 'Data type', 'Status', 'Date', 'Raised by', 'Assigned to', '']}
                  rows={filteredQueries.map(query => {
                    const queryDataType = dataTypes?.find(d => String(d._id) === String(query.dataType))
                    return [
                      <Stack key={`title-${query._id}`} direction='row' alignItems='center' gap={2}>
                        <Text>{query.title}</Text>
                        {query.commentCount > 0 &&
                          <Badge colorPalette='gray' title={`${query.commentCount} comment(s)`}>
                            <Icon as={FaRegComments} mr={1} />{query.commentCount}
                          </Badge>}
                      </Stack>,
                      !query.dataType
                        ? <Badge key={`datatype-${query._id}`} colorPalette='purple'>Whole response</Badge>
                        : queryDataType
                          ? <Badge key={`datatype-${query._id}`} colorPalette='blue'>{queryDataType.name} ({queryDataType.type})</Badge>
                          : <Badge key={`datatype-${query._id}`} colorPalette='orange'>Unknown data type</Badge>,
                      <Badge key={query._id} colorPalette={query.status === 'open' ? 'red' : 'green'}>{query.status}</Badge>,
                      utils.formatDateTime(query.createdAt),
                      query.raisedByUser
                        ? (
                          <UserChip compact user={query.raisedByUser} />)
                        : (
                            'Unknown user'),
                      (query.assigneesUsers?.length > 0)
                        ? (
                          <Stack direction='row' gap={2} wrap='wrap'>
                            {query.assigneesUsers.map(u => (
                              <UserChip key={u._id} compact user={u} />
                            ))}
                          </Stack>
                          )
                        : (
                          <Badge colorPalette='orange'>No one</Badge>),
                      <Box key={query._id} display='flex' justifyContent='end'>
                        {canResolve(query) && (
                          <ConfirmButton
                            header='Resolve this query?'
                            body='It will be closed and moved out of the open queries.'
                            actionColor='green'
                            onConfirm={() => resolveQuery(query._id)}
                            trigger={
                              <Button
                                size='sm'
                                variant='ghost'
                                colorPalette='green'
                                title='Resolve this query'
                                loading={resolvingIds.includes(query._id)}
                              >
                                <Icon as={FaCheck} />
                              </Button>
                            }
                          />
                        )}
                      </Box>
                    ]
                  })}
                />)}
          </Stack>)
        : (
            !loadError &&
              <EmptyBox icon='box' title='No queries have been raised yet' description='Queries raised automatically or by staff will appear here.' />)}

      {managedQuery &&
        <QueryManager
          query={managedQuery}
          trial={trial}
          currentUser={user}
          users={users}
          dataTypes={dataTypes}
          onChange={updateQuery}
          onClose={closeManager}
        />}
    </Stack>
  )
}

function QueryManager ({ query, trial, currentUser, onChange, users, dataTypes, onClose }) {
  const [status, setStatus] = useState(query.status)
  const [assignees, setAssignees] = useState(query.assignees || [])
  const [notes, setNotes] = useState(query.notes || '')
  const [savingNotes, setSavingNotes] = useState(false)
  // Bumped when something outside the thread writes to it, so the Comments tab
  // picks up the comment that records a status change.
  const [commentsVersion, setCommentsVersion] = useState(0)
  const queryDataType = dataTypes?.find(d => String(d._id) === String(query.dataType))
  const notesChanged = notes !== (query.notes || '')

  const isAssignee = u => (query.assignees || []).some(a => String(a) === String(u?._id))

  // Anyone who can act on the query can comment on it — including the people it
  // is assigned to, who may hold no queries permission at all. This mirrors the
  // API's check; the server is still the authority.
  const canComment = utils.canWriteTrialQueries(currentUser, trial) || isAssignee(currentUser)

  // Who can be mentioned *on this query*. The API keeps only mentions of people
  // who can open it and drops the rest silently, so offering anyone else would
  // show a name in the posted comment that notified nobody. Assignees count
  // whether or not they hold a queries permission — being assigned is what
  // gives them access — and they are the people you most want to be able to
  // name, so they can't be left out.
  const mentionableUsers = users.filter(u => utils.isMentionableIn(u, 'queries') || isAssignee(u))

  // Status and assignees save the moment they change — there is no draft state
  // worth holding on to, and changing the assignees emails people, so it should
  // not be something you can do and then walk away from without noticing. The
  // control is put back if the save fails, so what is on screen is always what
  // was actually stored.
  function saveField (data, revert, onSaved) {
    api.queries.update(query._id, data, d => {
      onChange(d)
      onSaved?.()
      utils.toast('success', 'Query updated')
    }, err => {
      revert()
      utils.toast('error', 'Unable to save query', err.message)
    })
  }

  function changeStatus (next) {
    const previous = status
    setStatus(next)
    // Resolving or reopening a query leaves a comment in its thread saying who
    // did it, so the thread has to be re-read to show it.
    saveField(
      { status: next },
      () => setStatus(previous),
      () => setCommentsVersion(v => v + 1)
    )
  }

  function changeAssignees (next) {
    const previous = assignees
    setAssignees(next)
    saveField({ assignees: next }, () => setAssignees(previous))
  }

  // Notes are free text, so they save on demand rather than on every keystroke.
  function saveNotes () {
    setSavingNotes(true)
    api.queries.update(query._id, { notes }, d => {
      setSavingNotes(false)
      onChange(d)
      utils.toast('success', 'Notes saved')
    }, err => {
      setSavingNotes(false)
      utils.toast('error', 'Unable to save notes', err.message)
    })
  }

  return (
    <SimpleModal
      size='xl'
      isOpen onClose={onClose}
      header='Manage query'
      body={
        <ResponsiveStack spacing={5}>
          <Card variant='elevated' gap={5} maxW='400px'>
            <Heading size='md'>Query details</Heading>
            <Box>

              <Text fontWeight='bold'>{query.participant ? 'Participant' : 'Questionnaire submission'}</Text>
              {query.participant
                ? <Link to={`/trials/${query.trial}/participants/${query.participant}`}>{query.participantParticipant?.id || 'View the participant'}</Link>
                : <Link to={`/trials/${query.trial}/poll-responses?stage=${query.stage}&response=${query.response}`}>View the submission</Link>}
            </Box>
            <Box>
              <Heading size='sm'>{query.title}</Heading>
              <Text>{query.description}</Text>
            </Box>

            <FormControl label='Data type'>
              {!query.dataType
                ? <Badge colorPalette='purple'>Whole response</Badge>
                : queryDataType
                  ? <Badge colorPalette='blue'>{queryDataType.name} ({queryDataType.type})</Badge>
                  : <Badge colorPalette='orange'>Unknown data type</Badge>}
            </FormControl>

            {query.dataType &&
              <FormControl label='Question'>
                <Textarea
                  readOnly
                  fontSize='sm'
                  rows={4}
                  value={query.componentTitle || ''}
                  placeholder='Unknown question'
                />
              </FormControl>}

            <FormControl label='Status' helperText='Saved as soon as you change it.'>
              <Select value={status} onChange={e => changeStatus(e.target.value)}>
                <option value='open'>Open</option>
                <option value='closed'>Resolved</option>
              </Select>
            </FormControl>

            <FormControl label='Assignees' helperText='Saved as soon as you change it. Anyone newly assigned is emailed.'>
              <UserChooser
                multiple
                placeholder='No one is assigned'
                users={users}
                selectedUsers={assignees}
                onChosen={u => changeAssignees(u || [])}
              />
            </FormControl>

            {query.dataType
              ? (
                <FormControl label='Response data' helperText={query.responseData?.score !== undefined && query.responseData?.score !== null ? `Scored ${query.responseData.score} in this response` : undefined}>
                  {/* A query can now be raised on a field that was asked and left
                        blank, which has no response data at all — say so rather than
                        stringifying undefined into a controlled textarea. */}
                  <Textarea
                    readOnly fontSize='sm' rows={5} fontFamily='monospace'
                    value={query.responseData
                      ? JSON.stringify(query.responseData.value ?? null, null, 2)
                      : 'No answer was given for this field.'}
                  />
                </FormControl>)
              : (
                <FormControl label='Response score' helperText='The score of the whole submission when this query was raised. Later submissions to the same response may have changed it.'>
                  <Text fontWeight='bold'>{query.responseScore ?? 'No score was recorded.'}</Text>
                </FormControl>)}
          </Card>

          <Stack gap={3} p={3} flex={1} minW={0}>
            <SimpleTabs
              tabs={[
                {
                  key: 'comments',
                  title: 'Comments',
                  content: (
                    <QueryComments
                      query={query}
                      trial={trial}
                      users={mentionableUsers}
                      currentUser={currentUser}
                      canWrite={canComment}
                      reloadKey={commentsVersion}
                      onCountChange={count => { if (count !== query.commentCount) onChange({ ...query, commentCount: count }) }}
                    />)
                },
                {
                  key: 'notes',
                  // The scratchpad the query has always had: one shared block
                  // of text, saved with the query. The thread is the
                  // conversation; this is the working note.
                  title: 'Notes',
                  content: (
                    <Stack gap={2}>
                      <Textarea
                        placeholder='Write notes or updates about this query...'
                        value={notes}
                        onChange={e => setNotes(e.target.value)}
                        rows={14}
                      />
                      <Stack direction='row' justifyContent='end' alignItems='center' gap={3}>
                        {notesChanged &&
                          <Text fontSize='xs' color='fg.muted'>Unsaved changes</Text>}
                        <Button
                          size='sm'
                          colorPalette='primary'
                          onClick={saveNotes}
                          loading={savingNotes}
                          disabled={!notesChanged}
                        >
                          Save notes
                        </Button>
                      </Stack>
                    </Stack>)
                }
              ]}
            />
          </Stack>
        </ResponsiveStack>
}
      footer={
          // Nothing in here is held as a draft any more — the status and
          // assignees save themselves, comments post as you write them, and the
          // notes have their own button — so there is nothing to cancel or
          // confirm on the way out.
        <Stack direction='row' justifyContent='end'>
          <Button onClick={onClose}>Close</Button>
        </Stack>
}
    />
  )
}
